import { useEffect, useState, useRef } from "react";
import { api, formatIDR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Upload, Download, Wand2, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";

// Label tampilan kategori alat radiologi (X-Ray dikenal sebagai layanan Rontgen)
const radCatLabel = (c) => (c === "X-Ray" ? "Rontgen (X-Ray)" : c);

const emptyForm = {
    code: "",
    name: "",
    category: "",
    unit_cost: 0,
    unit_cost_lanjutan: 0,
    jasa_medis: 0,
    price: 0,
    price_paket: 0,
    notes: "",
};

export default function ExamMasterList({ kind, title, subtitle, testidPrefix, hasUnitCost = true, showJasaMedis = true }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [masterUnitCost, setMasterUnitCost] = useState(0);
    const [radCategories, setRadCategories] = useState([]);
    const [roomResults, setRoomResults] = useState([]); // fasilitas: NUC per kelas kamar
    const [sort, setSort] = useState({ key: "code", dir: "asc" });
    const [pageSize, setPageSize] = useState(50);
    // Radiologi & Fasilitas: punya unit cost per kategori (dropdown kategori + auto-UC)
    const hasCatUC = kind === "radiology" || kind === "fasilitas";
    const [page, setPage] = useState(1);
    const fileRef = useRef();

    async function load() {
        setLoading(true);
        try {
            if (hasUnitCost) {
                const [ex, uc] = await Promise.all([
                    api.get(`/exams/${kind}`),
                    api.get(`/unit-cost/${kind}`),
                ]);
                setItems(ex.data);
                setMasterUnitCost(uc.data?.result?.unit_cost || 0);
                setRadCategories(uc.data?.result?.results_by_category || []);
                if (kind === "fasilitas") {
                    try {
                        const room = await api.get("/room-unit-cost");
                        setRoomResults(
                            (room.data?.results || []).filter((r) => (r.unit_cost || 0) > 0)
                        );
                    } catch {
                        setRoomResults([]);
                    }
                }
            } else {
                const ex = await api.get(`/exams/${kind}`);
                setItems(ex.data);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [kind]);

    function openCreate() {
        setEditing(null);
        setForm(emptyForm);
        setDialogOpen(true);
    }

    function openEdit(item) {
        setEditing(item);
        setForm({
            code: item.code,
            name: item.name,
            category: item.category || "",
            unit_cost: item.unit_cost,
            unit_cost_lanjutan: item.unit_cost_lanjutan || 0,
            jasa_medis: item.jasa_medis || 0,
            price: item.price,
            price_paket: item.price_paket || 0,
            notes: item.notes || "",
        });
        setDialogOpen(true);
    }

    function applyMasterUnitCost(value, label) {
        const v = Number(value) || 0;
        if (!v) {
            toast.error("Master Unit Cost belum tersedia. Isi di menu Unit Cost terlebih dahulu.");
            return;
        }
        setForm((f) => ({
            ...f,
            unit_cost: Math.round(v),
            ...(label && hasCatUC ? { category: label } : {}),
        }));
        toast.success(
            label
                ? `Unit Cost Dasar (${label}) diisi ${formatIDR(v)}`
                : `Unit Cost Dasar diisi ${formatIDR(v)}`
        );
    }

    async function save() {
        try {
            const payload = {
                ...form,
                unit_cost: Number(form.unit_cost) || 0,
                unit_cost_lanjutan: Number(form.unit_cost_lanjutan) || 0,
                jasa_medis: Number(form.jasa_medis) || 0,
                price: Number(form.price) || 0,
                price_paket: Number(form.price_paket) || 0,
            };
            if (editing) {
                await api.put(`/exams/${kind}/${editing.id}`, payload);
                toast.success("Pemeriksaan diperbarui");
            } else {
                await api.post(`/exams/${kind}`, payload);
                toast.success("Pemeriksaan ditambahkan");
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function remove(item) {
        if (!window.confirm(`Hapus ${item.name}?`)) return;
        try {
            await api.delete(`/exams/${kind}/${item.id}`);
            toast.success("Dihapus");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    // Bersihkan angka yang datang dari Excel: bisa berupa number, atau string
    // seperti "Rp 25.000", "25.000,50", "25000". Titik dianggap ribuan, koma desimal.
    function parseNumber(v) {
        if (v == null || v === "") return 0;
        if (typeof v === "number") return Number.isFinite(v) ? v : 0;
        const s = String(v)
            .replace(/[^0-9,.\-]/g, "") // buang Rp, spasi, dsb
            .replace(/\.(?=\d{3}(\D|$))/g, "") // hapus titik ribuan
            .replace(/,/g, "."); // koma → desimal
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
    }

    async function handleImport(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: "array" });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
            const rows = raw
                .map((r) => {
                    const norm = {};
                    for (const k of Object.keys(r)) {
                        norm[String(k).trim().toLowerCase().replace(/\s+/g, "_")] = r[k];
                    }
                    const row = {
                        code: String(norm.code || norm.kode || "").trim(),
                        name: String(norm.name || norm.nama || "").trim(),
                        category: String(norm.category || norm.kategori || "").trim(),
                        unit_cost: parseNumber(norm.unit_cost || norm.unit_cost_dasar),
                        unit_cost_lanjutan: parseNumber(
                            norm.unit_cost_lanjutan || norm.unitcostlanjutan
                        ),
                        price: parseNumber(
                            norm.price || norm.harga || norm.harga_jual || norm.harga_jual_rs
                        ),
                        price_paket: parseNumber(
                            norm.price_paket || norm.harga_jual_paket || norm.harga_paket
                        ),
                        notes: String(norm.notes || norm.catatan || ""),
                    };
                    if (showJasaMedis) {
                        row.jasa_medis = parseNumber(norm.jasa_medis || norm.jasamedis);
                    }
                    return row;
                })
                .filter((r) => r.code && r.name && !r.code.startsWith("#"));
            if (!rows.length) {
                toast.error("File tidak berisi data valid. Kolom minimal: code, name");
                return;
            }
            const res = await api.post(`/exams/${kind}/bulk-import`, { items: rows });
            const { inserted = 0, updated = 0 } = res.data || {};
            toast.success(
                `Import selesai: ${inserted} data baru, ${updated} diperbarui`
            );
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            e.target.value = "";
        }
    }

    function buildHeaderCols() {
        return [
            "code",
            "name",
            "category",
            "unit_cost",
            "unit_cost_lanjutan",
            ...(showJasaMedis ? ["jasa_medis"] : []),
            "price",
            "price_paket",
            "notes",
        ];
    }

    function exportExcel() {
        if (!items.length) {
            toast.error("Tidak ada data untuk diexport");
            return;
        }
        const headerCols = buildHeaderCols();
        const data = [...items]
            .sort((a, b) => String(a.code).localeCompare(String(b.code), "id", { numeric: true }))
            .map((i) => {
                const row = {
                    code: i.code,
                    name: i.name,
                    category: i.category || "",
                    unit_cost: Number(i.unit_cost) || 0,
                    unit_cost_lanjutan: Number(i.unit_cost_lanjutan) || 0,
                    price: Number(i.price) || 0,
                    price_paket: Number(i.price_paket) || 0,
                    notes: i.notes || "",
                };
                if (showJasaMedis) row.jasa_medis = Number(i.jasa_medis) || 0;
                return row;
            });
        const ws = XLSX.utils.json_to_sheet(data, { header: headerCols });
        ws["!cols"] = headerCols.map((h) =>
            h === "name" || h === "notes" ? { wch: 40 } : { wch: 18 }
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Master");
        const today = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `master-${kind}-${today}.xlsx`);
        toast.success(
            `${data.length} data diexport. Isi kolom price_paket lalu Import Excel untuk update massal.`
        );
    }

    function downloadTemplate() {
        const prefix =
            { lab: "LAB", radiology: "RAD", jasa: "JAS", obat: "OBT", fasilitas: "FAS" }[kind] || "ITM";
        const sampleCategory =
            { lab: "Hematologi", radiology: "Rontgen", jasa: "Tindakan", obat: "BMHP", fasilitas: "Ruang Kelas 3" }[kind] ||
            "Umum";
        const sample = [
            {
                code: `${prefix}-901`,
                name: "Contoh Item 1",
                category: sampleCategory,
                unit_cost: 25000,
                unit_cost_lanjutan: 0,
                jasa_medis: 10000,
                price: 55000,
                price_paket: 50000,
                notes: "Isi unit_cost dari master unit cost",
            },
            {
                code: `${prefix}-902`,
                name: "Contoh Item Rujukan",
                category: "Rujukan Luar",
                unit_cost: 0,
                unit_cost_lanjutan: 120000,
                jasa_medis: 0,
                price: 200000,
                price_paket: 180000,
                notes: "Dirujuk ke RS lain",
            },
        ];
        const headerCols = buildHeaderCols();
        if (!showJasaMedis) {
            for (const row of sample) delete row.jasa_medis;
        }
        // Baris petunjuk (akan diabaikan saat import karena tidak punya code/name valid)
        const jasaHint = showJasaMedis ? "Rp – opsional" : "";
        const RAD_CATEGORIES =
            "Rontgen, USG, CT Scan, MRI, Mamografi, Fluoroskopi, Densitometri Tulang, Kedokteran Nuklir, Echocardiography";
        const FAS_CATEGORIES =
            "Ruang Kelas 3, Ruang Kelas 2, Ruang Kelas 1, Ruang VIP, Ruang VVIP, NON Kelas, HCU, ICU, Perinatologi, ISOLASI, NICU, Ruang OK, Ruang VK";
        const instructionRow = {
            code: "# PETUNJUK",
            name: "Wajib isi code & name. Kolom angka: hanya angka (tanpa 'Rp' / titik).",
            category:
                kind === "radiology"
                    ? `Pilih salah satu: ${RAD_CATEGORIES}`
                    : kind === "fasilitas"
                    ? `Pilih salah satu: ${FAS_CATEGORIES}`
                    : "Opsional",
            unit_cost:
                kind === "radiology" || kind === "fasilitas"
                    ? "Kosongkan → otomatis terisi dari UC kategori"
                    : "Rp – dari master unit cost",
            unit_cost_lanjutan: "Rp – jika pemeriksaan dirujuk / lanjutan",
            ...(showJasaMedis ? { jasa_medis: jasaHint } : {}),
            price: "Rp – harga jual RS",
            price_paket: "Rp – harga jual paket (basis margin)",
            notes: "Total Unit Cost otomatis dihitung (tidak perlu diisi)",
        };
        if (kind === "radiology") {
            sample[0].unit_cost = "";
            sample[0].notes = "unit_cost kosong → otomatis dari UC kategori Rontgen";
            sample[1].category = "USG";
            sample[1].notes = "unit_cost kosong → otomatis dari UC kategori USG";
            sample[1].unit_cost = "";
            sample[1].unit_cost_lanjutan = 0;
        }
        if (kind === "fasilitas") {
            sample[0].category = "Ruang Kelas 3";
            sample[0].unit_cost = "";
            sample[0].notes = "unit_cost kosong → otomatis dari UC kategori Ruang Kelas 3";
            sample[1].category = "Ruang OK";
            sample[1].notes = "unit_cost kosong → otomatis dari UC kategori Ruang OK";
            sample[1].unit_cost = "";
            sample[1].unit_cost_lanjutan = 0;
        }
        const dataForSheet = [instructionRow, ...sample];
        const ws = XLSX.utils.json_to_sheet(dataForSheet, { header: headerCols });
        ws["!cols"] = headerCols.map((h) =>
            h === "name" || h === "notes" ? { wch: 40 } : { wch: 18 }
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, `template-${kind}.xlsx`);
    }

    const filtered = items.filter(
        (i) =>
            i.name.toLowerCase().includes(search.toLowerCase()) ||
            i.code.toLowerCase().includes(search.toLowerCase()) ||
            (i.category || "").toLowerCase().includes(search.toLowerCase())
    );

    const sortKey = sort.key;
    const sortDir = sort.dir === "desc" ? -1 : 1;
    const sorted = [...filtered].sort((a, b) => {
        const va = getSortValue(a, sortKey);
        const vb = getSortValue(b, sortKey);
        if (typeof va === "number" && typeof vb === "number") {
            return (va - vb) * sortDir;
        }
        return String(va).localeCompare(String(vb), "id", { numeric: true }) * sortDir;
    });

    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, totalPages);
    const start = (currentPage - 1) * pageSize;
    const paged = sorted.slice(start, start + pageSize);

    function toggleSort(key) {
        setSort((s) =>
            s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
        );
        setPage(1);
    }

    function getSortValue(row, key) {
        if (key === "total_uc") {
            return (
                Number(row.unit_cost || 0) +
                Number(row.unit_cost_lanjutan || 0) +
                (showJasaMedis ? Number(row.jasa_medis || 0) : 0)
            );
        }
        if (key === "margin") {
            const tc =
                Number(row.unit_cost || 0) +
                Number(row.unit_cost_lanjutan || 0) +
                Number(row.jasa_medis || 0);
            const pp = Number(row.price_paket || 0);
            return pp > 0 ? ((pp - tc) / pp) * 100 : -Infinity;
        }
        const numericKeys = ["unit_cost", "unit_cost_lanjutan", "jasa_medis", "price", "price_paket"];
        if (numericKeys.includes(key)) return Number(row[key] || 0);
        return String(row[key] || "").toLowerCase();
    }

    function SortableHead({ label, sortKey: k, align = "left" }) {
        const active = sortKey === k;
        const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
        return (
            <TableHead
                className={`label-uppercase select-none cursor-pointer group hover:text-[#1A362D] ${
                    align === "right" ? "text-right" : ""
                }`}
                onClick={() => toggleSort(k)}
                data-testid={`${testidPrefix}-sort-${k}`}
            >
                <span
                    className={`inline-flex items-center gap-1 ${
                        align === "right" ? "justify-end" : ""
                    } ${active ? "text-[#1A362D]" : ""}`}
                >
                    {label}
                    <Icon size={12} className={active ? "opacity-100" : "opacity-40 group-hover:opacity-70"} />
                </span>
            </TableHead>
        );
    }

    return (
        <div className="space-y-6" data-testid={`${testidPrefix}-page`}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="label-uppercase mb-2">Master Data</div>
                    <h1 className="font-heading text-4xl tracking-tight font-semibold">{title}</h1>
                    <p className="text-[#4A5E57] mt-2">{subtitle}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        ref={fileRef}
                        onChange={handleImport}
                        className="hidden"
                        data-testid={`${testidPrefix}-import-input`}
                    />
                    <Button
                        variant="outline"
                        onClick={exportExcel}
                        data-testid={`${testidPrefix}-export-btn`}
                        className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                    >
                        <FileSpreadsheet size={16} className="mr-2" /> Export Excel
                    </Button>
                    <Button
                        variant="outline"
                        onClick={downloadTemplate}
                        data-testid={`${testidPrefix}-template-btn`}
                        className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                    >
                        <Download size={16} className="mr-2" /> Template Excel
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => fileRef.current?.click()}
                        data-testid={`${testidPrefix}-import-btn`}
                        className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA]"
                    >
                        <Upload size={16} className="mr-2" /> Import Excel
                    </Button>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                onClick={openCreate}
                                data-testid={`${testidPrefix}-add-btn`}
                                className="bg-[#245B4B] hover:bg-[#1A4236] text-white"
                            >
                                <Plus size={16} className="mr-2" /> Tambah
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader>
                                <DialogTitle>
                                    {editing ? "Edit Pemeriksaan" : "Tambah Pemeriksaan"}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Kode</Label>
                                        <Input
                                            value={form.code}
                                            data-testid={`${testidPrefix}-form-code`}
                                            onChange={(e) => setForm({ ...form, code: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label>Kategori</Label>
                                        {kind === "radiology" || kind === "fasilitas" ? (
                                            <Select
                                                value={form.category || undefined}
                                                onValueChange={(val) => {
                                                    const r = radCategories.find(
                                                        (x) => x.category === val
                                                    );
                                                    const room = !r
                                                        ? roomResults.find((x) => x.name === val)
                                                        : null;
                                                    const uc = Math.round(
                                                        r?.unit_cost || room?.unit_cost || 0
                                                    );
                                                    setForm((f) => ({
                                                        ...f,
                                                        category: val,
                                                        ...(uc > 0 ? { unit_cost: uc } : {}),
                                                    }));
                                                    if (uc > 0)
                                                        toast.success(
                                                            room
                                                                ? `Unit Cost Dasar otomatis dari NUC Kamar ${val}: ${formatIDR(uc)}`
                                                                : `Unit Cost Dasar otomatis dari kategori ${radCatLabel(val)}: ${formatIDR(uc)}`
                                                        );
                                                }}
                                            >
                                                <SelectTrigger
                                                    data-testid={`${testidPrefix}-form-category`}
                                                >
                                                    <SelectValue placeholder="Pilih kategori alat" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {form.category &&
                                                        !radCategories.some(
                                                            (r) => r.category === form.category
                                                        ) &&
                                                        !roomResults.some(
                                                            (r) => r.name === form.category
                                                        ) && (
                                                            <SelectItem value={form.category}>
                                                                {form.category} (kustom)
                                                            </SelectItem>
                                                        )}
                                                    {radCategories.map((r) => (
                                                        <SelectItem
                                                            key={r.category}
                                                            value={r.category}
                                                            data-testid={`${testidPrefix}-cat-opt-${r.category
                                                                .toLowerCase()
                                                                .replace(/\s+/g, "-")}`}
                                                        >
                                                            {radCatLabel(r.category)} —{" "}
                                                            {formatIDR(r.unit_cost)}
                                                        </SelectItem>
                                                    ))}
                                                    {kind === "fasilitas" &&
                                                        roomResults
                                                            .filter(
                                                                (r) =>
                                                                    !radCategories.some(
                                                                        (x) => x.category === r.name
                                                                    )
                                                            )
                                                            .map((r) => (
                                                                <SelectItem
                                                                    key={`room-${r.class_id}`}
                                                                    value={r.name}
                                                                    data-testid={`${testidPrefix}-cat-opt-room-${r.name
                                                                        .toLowerCase()
                                                                        .replace(/\s+/g, "-")}`}
                                                                >
                                                                    Kamar {r.name} (NUC) —{" "}
                                                                    {formatIDR(r.unit_cost)}
                                                                </SelectItem>
                                                            ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Input
                                                value={form.category}
                                                data-testid={`${testidPrefix}-form-category`}
                                                onChange={(e) =>
                                                    setForm({ ...form, category: e.target.value })
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <Label>Nama Pemeriksaan</Label>
                                    <Input
                                        value={form.name}
                                        data-testid={`${testidPrefix}-form-name`}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <Label>Unit Cost Dasar (Rp)</Label>
                                            {!hasUnitCost ? null : hasCatUC ? (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            type="button"
                                                            data-testid={`${testidPrefix}-form-uc-fetch`}
                                                            className="text-xs text-[#245B4B] hover:text-[#1A4236] font-medium inline-flex items-center gap-1"
                                                        >
                                                            <Wand2 size={12} /> Ambil dari Master
                                                            <ChevronDown size={12} />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-72">
                                                        <DropdownMenuLabel>
                                                            {kind === "fasilitas"
                                                                ? "Unit Cost Alat (per kategori)"
                                                                : "Pilih Kategori"}
                                                        </DropdownMenuLabel>
                                                        <DropdownMenuSeparator />
                                                        {radCategories.length === 0 ? (
                                                            <DropdownMenuItem disabled>
                                                                Belum ada data
                                                            </DropdownMenuItem>
                                                        ) : (
                                                            radCategories.map((r) => (
                                                                <DropdownMenuItem
                                                                    key={r.category}
                                                                    onClick={() =>
                                                                        applyMasterUnitCost(
                                                                            r.unit_cost,
                                                                            r.category
                                                                        )
                                                                    }
                                                                    data-testid={`${testidPrefix}-fetch-${r.category
                                                                        .toLowerCase()
                                                                        .replace(/\s+/g, "-")}`}
                                                                    className="flex items-center justify-between"
                                                                >
                                                                    <span>{r.category}</span>
                                                                    <span className="text-xs text-[#4A5E57] ml-3">
                                                                        {formatIDR(r.unit_cost)}
                                                                    </span>
                                                                </DropdownMenuItem>
                                                            ))
                                                        )}
                                                        {kind === "fasilitas" && (
                                                            <>
                                                                <DropdownMenuSeparator />
                                                                <DropdownMenuLabel>
                                                                    Unit Cost Kamar (per Hari Rawat)
                                                                </DropdownMenuLabel>
                                                                <DropdownMenuSeparator />
                                                                {roomResults.length === 0 ? (
                                                                    <DropdownMenuItem disabled>
                                                                        Belum ada NUC — isi di tab Unit Cost Kamar
                                                                    </DropdownMenuItem>
                                                                ) : (
                                                                    roomResults.map((r) => (
                                                                        <DropdownMenuItem
                                                                            key={`room-${r.class_id}`}
                                                                            onClick={() =>
                                                                                applyMasterUnitCost(
                                                                                    r.unit_cost,
                                                                                    r.name
                                                                                )
                                                                            }
                                                                            data-testid={`${testidPrefix}-fetch-room-${r.name
                                                                                .toLowerCase()
                                                                                .replace(/\s+/g, "-")}`}
                                                                            className="flex items-center justify-between"
                                                                        >
                                                                            <span>Kamar {r.name}</span>
                                                                            <span className="text-xs text-[#4A5E57] ml-3">
                                                                                {formatIDR(r.unit_cost)}
                                                                            </span>
                                                                        </DropdownMenuItem>
                                                                    ))
                                                                )}
                                                            </>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => applyMasterUnitCost(masterUnitCost)}
                                                    data-testid={`${testidPrefix}-form-uc-fetch`}
                                                    className="text-xs text-[#245B4B] hover:text-[#1A4236] font-medium inline-flex items-center gap-1"
                                                    title={`Ambil dari master Unit Cost (${formatIDR(masterUnitCost)})`}
                                                >
                                                    <Wand2 size={12} /> Ambil dari Master
                                                </button>
                                            )}
                                        </div>
                                        <Input
                                            type="number"
                                            value={form.unit_cost}
                                            data-testid={`${testidPrefix}-form-unit-cost`}
                                            onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                                        />
                                        <div className="text-xs text-[#6A9C89] mt-1">
                                            {!hasUnitCost
                                                ? "Harga modal / pembelian"
                                                : hasCatUC
                                                ? kind === "fasilitas"
                                                    ? `${radCategories.length} kategori alat + ${roomResults.length} kelas kamar tersedia`
                                                    : `${radCategories.length} kategori tersedia`
                                                : `Master saat ini: ${formatIDR(masterUnitCost)}`}
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Unit Cost Lanjutan (Rp)</Label>
                                        <Input
                                            type="number"
                                            value={form.unit_cost_lanjutan}
                                            data-testid={`${testidPrefix}-form-uc-lanjutan`}
                                            onChange={(e) =>
                                                setForm({ ...form, unit_cost_lanjutan: e.target.value })
                                            }
                                        />
                                        <div className="text-xs text-[#6A9C89] mt-1">
                                            Untuk pemeriksaan rujukan ke RS lain
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {showJasaMedis && (
                                        <div>
                                            <Label>Jasa Medis (Rp)</Label>
                                            <Input
                                                type="number"
                                                value={form.jasa_medis}
                                                data-testid={`${testidPrefix}-form-jasa-medis`}
                                                onChange={(e) => setForm({ ...form, jasa_medis: e.target.value })}
                                            />
                                        </div>
                                    )}
                                    <div className={showJasaMedis ? "" : "col-span-2"}>
                                        <Label>Harga Jual RS (Rp)</Label>
                                        <Input
                                            type="number"
                                            value={form.price}
                                            data-testid={`${testidPrefix}-form-price`}
                                            onChange={(e) => setForm({ ...form, price: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Harga Jual Paket (Rp)</Label>
                                    <Input
                                        type="number"
                                        value={form.price_paket}
                                        data-testid={`${testidPrefix}-form-price-paket`}
                                        onChange={(e) => setForm({ ...form, price_paket: e.target.value })}
                                    />
                                    <div className="text-xs text-[#6A9C89] mt-1">
                                        Basis perhitungan margin & harga paket di kalkulator
                                    </div>
                                </div>
                                <div>
                                    <Label>Catatan</Label>
                                    <Input
                                        value={form.notes}
                                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => setDialogOpen(false)}
                                    className="border-[#D1DCD8]"
                                >
                                    Batal
                                </Button>
                                <Button
                                    onClick={save}
                                    data-testid={`${testidPrefix}-form-save`}
                                    className="bg-[#245B4B] hover:bg-[#1A4236] text-white"
                                >
                                    Simpan
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="surface-card p-6">
                <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <Input
                        placeholder="Cari nama / kode / kategori..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        data-testid={`${testidPrefix}-search`}
                        className="max-w-sm"
                    />
                    <div className="flex items-center gap-2 text-sm text-[#4A5E57]">
                        <span>Tampil per</span>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            data-testid={`${testidPrefix}-page-size`}
                            className="border border-[#D1DCD8] rounded-md bg-white px-2 py-1.5 text-[#1A362D] focus:outline-none focus:ring-2 focus:ring-[#245B4B]"
                        >
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>baris</span>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-[#D1DCD8]">
                                <SortableHead label="Kode" sortKey="code" />
                                <SortableHead label="Nama" sortKey="name" />
                                <SortableHead label="Kategori" sortKey="category" />
                                <SortableHead label="Unit Cost Dasar" sortKey="unit_cost" align="right" />
                                <SortableHead label="Unit Cost Lanjutan" sortKey="unit_cost_lanjutan" align="right" />
                                {showJasaMedis && (
                                    <SortableHead label="Jasa Medis" sortKey="jasa_medis" align="right" />
                                )}
                                <SortableHead label="Total Unit Cost" sortKey="total_uc" align="right" />
                                <SortableHead label="Harga Jual RS" sortKey="price" align="right" />
                                <SortableHead label="Harga Jual Paket" sortKey="price_paket" align="right" />
                                <SortableHead label="Margin Awal" sortKey="margin" align="right" />
                                <TableHead className="label-uppercase text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={showJasaMedis ? 11 : 10} className="text-center py-8 text-[#6A9C89]">
                                        Memuat...
                                    </TableCell>
                                </TableRow>
                            ) : total === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={showJasaMedis ? 11 : 10} className="text-center py-8 text-[#6A9C89]">
                                        Tidak ada data.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paged.map((i) => {
                                    const jasa = i.jasa_medis || 0;
                                    const ucLanjutan = i.unit_cost_lanjutan || 0;
                                    const totalCost = (i.unit_cost || 0) + ucLanjutan + jasa;
                                    const pricePaket = i.price_paket || 0;
                                    const margin =
                                        pricePaket > 0
                                            ? ((pricePaket - totalCost) / pricePaket) * 100
                                            : null;
                                    return (
                                        <TableRow
                                            key={i.id}
                                            className="border-[#D1DCD8] hover:bg-[#F7F9F6]"
                                            data-testid={`${testidPrefix}-row-${i.code}`}
                                        >
                                            <TableCell className="font-mono text-xs">{i.code}</TableCell>
                                            <TableCell className="font-medium">{i.name}</TableCell>
                                            <TableCell className="text-[#4A5E57]">{i.category}</TableCell>
                                            <TableCell className="text-right">{formatIDR(i.unit_cost)}</TableCell>
                                            <TableCell className="text-right text-[#4A5E57]">
                                                {formatIDR(ucLanjutan)}
                                            </TableCell>
                                            {showJasaMedis && (
                                                <TableCell className="text-right text-[#4A5E57]">
                                                    {formatIDR(jasa)}
                                                </TableCell>
                                            )}
                                            <TableCell
                                                className="text-right font-medium text-[#245B4B]"
                                                data-testid={`${testidPrefix}-total-uc-${i.code}`}
                                            >
                                                {formatIDR(totalCost)}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatIDR(i.price)}
                                            </TableCell>
                                            <TableCell
                                                className="text-right font-medium text-[#1A362D]"
                                                data-testid={`${testidPrefix}-price-paket-${i.code}`}
                                            >
                                                {pricePaket > 0 ? formatIDR(pricePaket) : "-"}
                                            </TableCell>
                                            <TableCell className="text-right text-[#4A5E57]">
                                                {margin == null ? "-" : `${margin.toFixed(1)}%`}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        onClick={() => openEdit(i)}
                                                        data-testid={`${testidPrefix}-edit-${i.code}`}
                                                        className="p-2 rounded-md hover:bg-[#E9EFEA] text-[#245B4B]"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => remove(i)}
                                                        data-testid={`${testidPrefix}-delete-${i.code}`}
                                                        className="p-2 rounded-md hover:bg-red-50 text-[#D9534F]"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
                {total > 0 && (
                    <div
                        className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-[#4A5E57]"
                        data-testid={`${testidPrefix}-pagination`}
                    >
                        <div>
                            Menampilkan {start + 1}–{Math.min(start + pageSize, total)} dari {total} data
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage <= 1}
                                data-testid={`${testidPrefix}-page-prev`}
                                className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA] disabled:opacity-40"
                            >
                                Sebelumnya
                            </Button>
                            <span
                                className="px-3 font-medium text-[#1A362D]"
                                data-testid={`${testidPrefix}-page-info`}
                            >
                                Hal {currentPage} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                                data-testid={`${testidPrefix}-page-next`}
                                className="border-[#D1DCD8] text-[#245B4B] hover:bg-[#E9EFEA] disabled:opacity-40"
                            >
                                Berikutnya
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
